<template>
  <el-dialog
    :title="
      device
        ? (device.friendlyName || device.identifier || device.deviceId) +
          ' — Qualification Detail'
        : 'Qualification Detail'
    "
    :visible.sync="visible"
    width="760px"
    @close="
      device = null
      detail = null
    "
  >
    <div v-loading="loading">
      <template v-if="detail">
        <!-- IQ Section -->
        <div class="detail-section-title" style="margin-top:0">
          Installation Qualification (IQ)
          <el-tag
            :type="statusTag(detail.iq && detail.iq.status)"
            size="small"
            style="margin-left:8px;vertical-align:middle"
          >
            {{ (detail.iq && detail.iq.status) || 'Not started' }}
          </el-tag>
        </div>

        <template v-if="detail.iq && sortedIqChecklist.length">
          <div
            v-for="item in sortedIqChecklist"
            :key="item.id"
            class="checklist-item"
          >
            <div class="checklist-item__head">
              <span class="checklist-item__label">{{
                item.label || item.checkName
              }}</span>
              <el-tag v-if="item.notApplicable" type="info" size="mini"
                >N/A</el-tag
              >
              <el-tag v-else-if="item.pass === true" type="success" size="mini"
                ><i class="el-icon-check" /> Pass</el-tag
              >
              <el-tag v-else-if="item.pass === false" type="danger" size="mini"
                ><i class="el-icon-close" /> Fail</el-tag
              >
              <el-tag v-else type="info" size="mini">Pending</el-tag>
            </div>
            <div
              v-if="formatMeasuredValue(item)"
              class="checklist-item__notes"
              style="font-family:monospace;font-size:12px"
            >
              {{ formatMeasuredValue(item) }}
            </div>
            <div
              v-if="item.pass === false && item.failureHint"
              class="checklist-item__hint"
            >
              <i class="el-icon-warning-outline" /> {{ item.failureHint }}
            </div>
          </div>
          <div
            v-if="detail.iq.photos && detail.iq.photos.length"
            style="margin-top:12px"
          >
            <div style="font-size:12px;color:#909399;margin-bottom:6px">
              Photos ({{ detail.iq.photos.length }})
            </div>
            <div class="checklist-item__photos">
              <a
                v-for="photo in detail.iq.photos"
                :key="photo.photoId"
                :href="photo.storageUrl"
                target="_blank"
                class="photo-link"
              >
                <img :src="photo.storageUrl" class="photo-thumb" />
              </a>
            </div>
          </div>
        </template>
        <div v-else class="empty-hint">No IQ data yet.</div>

        <!-- OQ Section -->
        <div class="detail-section-title" style="margin-top:24px">
          Operational Qualification (OQ)
          <el-tag
            :type="statusTag(detail.oq && detail.oq.status)"
            size="small"
            style="margin-left:8px;vertical-align:middle"
          >
            {{ (detail.oq && detail.oq.status) || 'Not started' }}
          </el-tag>
        </div>

        <template v-if="detail.oq && sortedOqChecklist.length">
          <div
            v-for="item in sortedOqChecklist"
            :key="item.id"
            class="checklist-item"
          >
            <div class="checklist-item__head">
              <span class="checklist-item__label">{{
                item.label || item.checkName
              }}</span>
              <el-tag v-if="item.notApplicable" type="info" size="mini"
                >N/A</el-tag
              >
              <el-tag v-else-if="item.pass === true" type="success" size="mini"
                ><i class="el-icon-check" /> Pass</el-tag
              >
              <el-tag v-else-if="item.pass === false" type="danger" size="mini"
                ><i class="el-icon-close" /> Fail</el-tag
              >
              <el-tag v-else type="info" size="mini">Pending</el-tag>
            </div>
            <div
              v-if="formatMeasuredValue(item)"
              class="checklist-item__notes"
              style="font-family:monospace;font-size:12px"
            >
              {{ formatMeasuredValue(item) }}
            </div>
            <div
              v-if="item.pass === false && item.failureHint"
              class="checklist-item__hint"
            >
              <i class="el-icon-warning-outline" /> {{ item.failureHint }}
            </div>
          </div>
        </template>
        <div v-else class="empty-hint">No OQ data yet.</div>
      </template>
      <div v-else-if="!loading" class="empty-hint" style="padding:24px 0">
        No qualification data for this device yet.
      </div>
    </div>
  </el-dialog>
</template>

<script>
import LoraApiService from '@/services/LoraApiService'

const DEVICE_TYPE_MAP = {
  Gateway: 'gateway',
  EndDevice: 'end-device',
  Node: 'node',
  DashboardDisplay: 'dashboard-display',
  BleDevice: 'ble-device'
}

export default {
  name: 'DeviceQualDialog',
  props: {
    jobId: { type: String, required: true }
  },
  data() {
    return {
      visible: false,
      loading: false,
      device: null,
      detail: null
    }
  },
  computed: {
    sortedIqChecklist() {
      if (!this.detail || !this.detail.iq) return []
      return (this.detail.iq.checklist || []).slice().sort(function(a, b) {
        return (a.displayOrder || 0) - (b.displayOrder || 0)
      })
    },
    sortedOqChecklist() {
      if (!this.detail || !this.detail.oq) return []
      return (this.detail.oq.checklist || []).slice().sort(function(a, b) {
        return (a.displayOrder || 0) - (b.displayOrder || 0)
      })
    }
  },
  methods: {
    open(device) {
      this.device = device
      this.detail = null
      this.visible = true
      this.fetchDetail(device)
    },
    async fetchDetail(device) {
      this.loading = true
      try {
        var apiType =
          DEVICE_TYPE_MAP[device.deviceType] || device.deviceType.toLowerCase()
        var res = await LoraApiService.getDeviceQualification(
          this.jobId,
          apiType,
          device.deviceId || device.id
        )
        this.detail = res.data
      } catch (e) {
        this.$message.error('Failed to load qualification detail')
      } finally {
        this.loading = false
      }
    },
    formatMeasuredValue(item) {
      var val = item.measuredValue
      if (!val) return null
      if (val === 'True' || val === 'False') return null
      try {
        var parsed = JSON.parse(val)
        if (Array.isArray(parsed)) return parsed.join(', ')
        if (parsed && typeof parsed === 'object') {
          if (parsed.error) return parsed.error
          if (parsed.notes) return parsed.notes
          return null
        }
      } catch (e) {
        /* not JSON */
      }
      return val
    },
    statusTag(s) {
      return (
        { Pass: 'success', Fail: 'danger', InProgress: 'warning' }[s] || 'info'
      )
    }
  }
}
</script>

<style lang="scss" scoped>
.detail-section-title {
  font-size: 14px;
  font-weight: 700;
  color: #1a2332;
  margin: 16px 0 10px;
  padding-bottom: 6px;
  border-bottom: 1px solid #e4e7ed;
}
.checklist-item {
  padding: 10px 14px;
  background: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 6px;
  margin-bottom: 8px;
  &__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
  }
  &__label {
    font-size: 13px;
    font-weight: 500;
    color: #1a2332;
    flex: 1;
  }
  &__notes {
    font-size: 12px;
    color: #606266;
    margin-top: 6px;
    line-height: 1.5;
  }
  &__hint {
    font-size: 12px;
    color: #e6a23c;
    margin-top: 6px;
    line-height: 1.5;
  }
  &__photos {
    display: flex;
    gap: 8px;
    margin-top: 8px;
    flex-wrap: wrap;
  }
}
.photo-link {
  display: block;
  border-radius: 4px;
  overflow: hidden;
  border: 1px solid #e4e7ed;
}
.photo-thumb {
  width: 80px;
  height: 80px;
  object-fit: cover;
  display: block;
}
.empty-hint {
  color: #c0c4cc;
  font-size: 13px;
  padding: 12px 0;
}
</style>
